import { useEffect } from 'react';
import { useCallDetailQuery } from '../../api/queries';
import { formatDuration } from '../../lib/format';
import type { Call } from '../../types';

type Props = {
  callId: number | null;
  onClose: () => void;
};

function statusOf(c: Call): { className: string; label: string; note?: string } {
  if (c.voicemail) return { className: 'voicemail', label: 'Voicemail' };
  if (c.ivrDetected) {
    return {
      className: 'ivr',
      label: 'IVR Only',
      note: 'Caller reached the IVR only — not counted toward answer rate.',
    };
  }
  if (c.answered) return { className: 'answered', label: 'Answered' };
  return { className: 'missed', label: 'Missed' };
}

function sentimentClass(s: string | null | undefined): string {
  const v = (s ?? '').toLowerCase();
  if (v.includes('positive')) return 'positive';
  if (v.includes('negative')) return 'negative';
  return 'neutral';
}

function tagClass(tag: string): string {
  if (tag === 'Good Lead') return 'good-lead';
  if (tag === 'Schedule booked') return 'booked';
  return '';
}

export default function CallDetailDrawer({ callId, onClose }: Props) {
  const isOpen = callId !== null;
  const { data, isLoading, error } = useCallDetailQuery(callId);

  useEffect(() => {
    function onEsc(e: KeyboardEvent) {
      if (e.key === 'Escape' && isOpen) onClose();
    }
    document.addEventListener('keydown', onEsc);
    return () => document.removeEventListener('keydown', onEsc);
  }, [isOpen, onClose]);

  return (
    <>
      <div className={`call-detail-overlay ${isOpen ? 'open' : ''}`} onClick={onClose} />
      <div className={`call-detail-panel ${isOpen ? 'open' : ''}`}>
        <div className="cdp-header">
          <h3>Call Detail</h3>
          <button className="cdp-close" onClick={onClose} aria-label="Close">
            ×
          </button>
        </div>
        <div className="cdp-body">
          {isLoading && (
            <div className="listings-loading">
              <div className="spinner" />
              Loading call detail...
            </div>
          )}
          {error && (
            <div className="reviews-unavailable">
              <p>Failed to load call: {(error as Error).message}</p>
            </div>
          )}
          {data && <CallDetailBody call={data} />}
        </div>
      </div>
    </>
  );
}

function CallDetailBody({ call: c }: { call: Call }) {
  const dt = new Date(c.startTime);
  const status = statusOf(c);
  const dateLabel =
    dt.toLocaleDateString('en-US', {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    }) +
    ' at ' +
    dt.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });

  const agentPct = Number(c.speakerPercentAgent) || 0;
  const custPct = Number(c.speakerPercentCustomer) || 0;
  const ci = c.clientInference;
  const hasInference =
    !!ci && (ci.patientName || ci.callerName || ci.patientCondition || ci.careNeeds);

  return (
    <>
      <div className="cdp-section">
        <div className="cdp-section-title">Call Information</div>
        <DetailField label="Date" value={dateLabel} />
        <DetailField
          label="Status"
          value={
            <>
              <span className={`call-status-dot ${status.className}`} />
              {status.label}
              {status.note && (
                <div style={{ fontSize: 11, color: '#8a95a5', marginTop: 2 }}>{status.note}</div>
              )}
            </>
          }
        />
        <DetailField
          label="Caller"
          value={
            <>
              {c.customerName || 'Unknown'}
              {c.customerPhone && (
                <span style={{ color: '#8a95a5' }}> ({c.customerPhone})</span>
              )}
            </>
          }
        />
        <DetailField label="Duration" value={formatDuration(c.duration)} />
        <DetailField label="Source" value={c.source || 'Direct'} />
        {c.callrailSentiment && (
          <DetailField
            label="Sentiment"
            value={
              <span className={`call-sentiment ${sentimentClass(c.callrailSentiment)}`}>
                {c.callrailSentiment}
              </span>
            }
          />
        )}
        {c.callrailTags && c.callrailTags.length > 0 && (
          <DetailField
            label="Tags"
            value={
              <>
                {c.callrailTags.map((t, i) => (
                  <span key={i} className={`call-tag ${tagClass(t)}`}>{t}</span>
                ))}
              </>
            }
          />
        )}
        {(agentPct > 0 || custPct > 0) && (
          <DetailField
            label="Talk Time"
            value={
              <div style={{ flex: 1 }}>
                <div className="cdp-talk-time">
                  <div className="cdp-talk-agent" style={{ width: `${agentPct}%` }} />
                  <div className="cdp-talk-customer" style={{ width: `${custPct}%` }} />
                </div>
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    fontSize: 11,
                    color: '#5a6a7a',
                  }}
                >
                  <span>Agent {agentPct}%</span>
                  <span>Customer {custPct}%</span>
                </div>
              </div>
            }
          />
        )}
      </div>

      {c.hasRecording && (
        <div className="cdp-section">
          <div className="cdp-section-title">Recording</div>
          <audio
            className="cdp-audio-player"
            controls
            preload="none"
            src={`/api/pulse/calls/${c.id}/recording`}
          >
            Your browser does not support audio.
          </audio>
        </div>
      )}

      {c.callSummary && (
        <div className="cdp-section">
          <div className="cdp-section-title">AI Summary</div>
          <div className="cdp-summary">{c.callSummary}</div>
        </div>
      )}

      {hasInference && ci && (
        <div className="cdp-section">
          <div className="cdp-section-title">Patient Information (AI Extracted)</div>
          <div className="cdp-inference-card">
            {ci.patientName && (
              <DetailField
                label="Patient"
                value={ci.patientName + (ci.patientAge ? `, ${ci.patientAge}` : '')}
              />
            )}
            {ci.callerName && (
              <DetailField
                label="Caller"
                value={ci.callerName + (ci.patientRelationship ? ` (${ci.patientRelationship})` : '')}
              />
            )}
            {ci.patientCondition && <DetailField label="Condition" value={ci.patientCondition} />}
            {ci.careNeeds && <DetailField label="Care Needs" value={ci.careNeeds} />}
            <div style={{ marginTop: 8, fontSize: 10, color: '#8a95a5' }}>
              Confidence: {ci.confidence ?? 'N/A'}
            </div>
          </div>
        </div>
      )}

      {c.transcript && (
        <div className="cdp-section">
          <div className="cdp-section-title">Transcript</div>
          <div className="cdp-transcript">{c.transcript}</div>
        </div>
      )}
    </>
  );
}

function DetailField({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="cdp-field">
      <div className="cdp-field-label">{label}</div>
      <div className="cdp-field-value">{value}</div>
    </div>
  );
}
